import { useCallback, useEffect, useRef, useState } from 'react'

import styles from './VideoStreamPlayer.module.css'

interface VideoStreamPlayerProps {
  src: string
}

interface WsMessage {
  type: string
  value?: string
  sdpMid?: string
  sdpMLineIndex?: number
}

type StreamMode = 'INIT' | 'LOADING' | 'MSE' | 'HLS' | 'MP4' | 'RTC' | 'MJPEG' | 'ERROR'

const VideoStreamPlayer = ({ src }: VideoStreamPlayerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const pcRef = useRef<RTCPeerConnection | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const mountedRef = useRef(true)
  const playPromiseRef = useRef<Promise<void> | null>(null)
  const intentionalCloseRef = useRef(false)

  const mediaSourceRef = useRef<MediaSource | null>(null)
  const sourceBufferRef = useRef<SourceBuffer | null>(null)
  const updateEndHandlerRef = useRef<(() => void) | null>(null)

  const [mode, setMode] = useState<StreamMode>('INIT')
  const [status, setStatus] = useState('')

  // Храним mode в ref для использования в замыканиях (чтобы избежать stale closure)
  const modeRef = useRef(mode)
  modeRef.current = mode

  const RECONNECT_TIMEOUT = 15000

  // Настройки по умолчанию
  const modeConfig = 'webrtc,mse,hls,mjpeg'

  // --- Очистка MSE-состояния ---
  const resetMSE = useCallback(() => {
    if (sourceBufferRef.current && updateEndHandlerRef.current) {
      sourceBufferRef.current.removeEventListener('updateend', updateEndHandlerRef.current)
      updateEndHandlerRef.current = null
    }

    if (mediaSourceRef.current && mediaSourceRef.current.readyState === 'open') {
      try {
        mediaSourceRef.current.endOfStream()
      } catch (_) {
        // Игнорируем ошибки при закрытии
      }
    }

    mediaSourceRef.current = null
    sourceBufferRef.current = null
  }, [])

  // Безопасный вызов play() с защитой от DOMException при переключении режимов
  const safePlay = useCallback(() => {
    const video = videoRef.current
    if (!video) return

    const promise = video.play()
    playPromiseRef.current = promise

    if (promise !== undefined) {
      promise.catch((err: DOMException) => {
        // Игнорируем, если Promise уже не актуален (переключение режима / размонтирование)
        if (playPromiseRef.current !== promise) return
        if (!mountedRef.current) return
        console.warn('[VideoPlayer] Play interrupted:', err.message)
      })
    }
  }, [])

  useEffect(() => {
    mountedRef.current = true

    if (!src) return

    setMode('LOADING')
    connect()

    return () => {
      mountedRef.current = false
      disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src])

  const connect = () => {
    // Сбрасываем флаг намеренного закрытия — теперь реконнект снова будет работать
    intentionalCloseRef.current = false

    // Сбрасываем MSE-состояние перед новым подключением
    resetMSE()

    const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws'
    const url = new URL(
      `${scheme}://${window.location.host}/api/websocket/rtc?src=${src}`
    )

    const ws = new WebSocket(url.toString())
    ws.binaryType = 'arraybuffer'
    wsRef.current = ws

    ws.onopen = () => {
      if (!mountedRef.current) return
      console.debug('[React-VideoRTC] WebSocket opened')
      handleOpen()
    }

    ws.onmessage = (event: MessageEvent) => {
      if (!mountedRef.current) return
      if (typeof event.data === 'string') {
        try {
          const msg: WsMessage = JSON.parse(event.data)
          handleMessage(msg)
        } catch (e) {
          console.error('Error parsing message:', e)
        }
      } else {
        handleBinaryData(event.data as ArrayBuffer)
      }
    }

    ws.onclose = () => {
      if (!mountedRef.current) return
      if (intentionalCloseRef.current) {
        console.debug('[React-VideoRTC] WebSocket closed intentionally')
        return
      }
      console.debug('[React-VideoRTC] WebSocket closed unexpectedly, scheduling reconnect')
      scheduleReconnect()
    }

    ws.onerror = (ev: Event) => {
      if (!mountedRef.current) return
      console.error('[React-VideoRTC] WebSocket error')
      setStatus('WebSocket Error')
      setMode('ERROR')
      console.log(ev)
    }
  }

  const disconnect = () => {
    intentionalCloseRef.current = true

    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = null
    }

    if (wsRef.current) {
      // Обнуляем обработчики, чтобы вызов close() на CONNECTING WebSocket
      // не вызывал onerror/onclose (React StrictMode double-mount)
      wsRef.current.onopen = null
      wsRef.current.onclose = null
      wsRef.current.onerror = null
      wsRef.current.onmessage = null
      wsRef.current.close()
      wsRef.current = null
    }

    if (pcRef.current) {
      const pc = pcRef.current
      pc.onicecandidate = null
      pc.onicecandidateerror = null
      pc.oniceconnectionstatechange = null
      pc.onconnectionstatechange = null
      pc.ontrack = null
      pc.getSenders().forEach(sender => sender.track?.stop())
      pc.close()
      pcRef.current = null
    }

    // Сбрасываем playPromise, чтобы ошибка от play() при размонтировании была проигнорирована
    playPromiseRef.current = null

    // Очищаем MSE-состояние
    resetMSE()

    if (videoRef.current) {
      videoRef.current.srcObject = null
      videoRef.current.src = ''
    }
  }

  const scheduleReconnect = () => {
    reconnectTimerRef.current = setTimeout(() => {
      if (!mountedRef.current) return
      console.log('Attempting to reconnect...')
      connect()
    }, RECONNECT_TIMEOUT)
  }

  const handleOpen = async () => {
    if (!mountedRef.current) return

    // Всегда запускаем MSE первым (он даёт видео быстрее всего)
    if (modeConfig.includes('mse') && 'MediaSource' in window) {
      console.log('[VideoPlayer] Starting MSE mode')
      setupMSE()
    }

    // Параллельно пробуем WebRTC (он качественнее)
    if (modeConfig.includes('webrtc') && 'RTCPeerConnection' in window) {
      console.log('[VideoPlayer] Starting WebRTC mode')
      await setupWebRTC()
      // Mode установится в ontrack, когда WebRTC реально подключится
    }

    // HLS — fallback для Safari/iOS (только если MSE не запущен)
    if (
      modeConfig.includes('hls') &&
      !mediaSourceRef.current &&
      videoRef.current?.canPlayType('application/vnd.apple.mpegurl')
    ) {
      console.log('[VideoPlayer] Starting HLS mode')
      send({ type: 'hls' })
      setMode('HLS')
    }

    // MJPEG — последний fallback (только если MSE и HLS не запущены)
    if (modeConfig.includes('mjpeg') && !mediaSourceRef.current) {
      console.log('[VideoPlayer] Starting MJPEG mode')
      send({ type: 'mjpeg' })
      setMode('MJPEG')
    }
  }

  // --- Отправка сообщений ---
  const send = (msg: Record<string, unknown>) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg))
    }
  }

  const handleMessage = (msg: WsMessage) => {
    if (!mountedRef.current) return

    switch (msg.type) {
      case 'error':
        if (msg.value?.includes('codecs not matched')) {
          console.warn('Сервер go2rtc:', msg.value)
        } else {
          setStatus(msg.value ?? '')
        }
        break

      case 'mse':
        // Сервер прислал информацию о поддерживаемых кодеках
        console.debug('[MSE] Получены кодеки от сервера:', msg.value)
        setMode('MSE')

        // Пробуем создать SourceBuffer с этими кодеками
        try {
          if (msg.value && mediaSourceRef.current) {
            // Удаляем старый listener перед созданием нового SourceBuffer
            if (sourceBufferRef.current && updateEndHandlerRef.current) {
              sourceBufferRef.current.removeEventListener('updateend', updateEndHandlerRef.current)
              updateEndHandlerRef.current = null
            }

            // Оставляем только видеокодеки (аудио не нужно)
            const videoCodecs = msg.value
              .split(',')
              .filter(codec => codec.startsWith('avc1') || codec.startsWith('hvc1') || codec.startsWith('hev1'))
              .join(',')

            if (videoCodecs) {
              sourceBufferRef.current = mediaSourceRef.current.addSourceBuffer(videoCodecs)
            }
          }

          // Создаём handler один раз и сохраняем ссылку на него
          if (sourceBufferRef.current && !updateEndHandlerRef.current) {
            const onUpdateEnd = () => {
              if (!mountedRef.current) return

              // Если SourceBuffer снова занят — ждём следующего updateend
              if (sourceBufferRef.current?.updating) return

              // Очищаем старые данные из буфера (держим только последние 3 секунды)
              if (sourceBufferRef.current && sourceBufferRef.current.buffered.length > 0) {
                const end = sourceBufferRef.current.buffered.end(sourceBufferRef.current.buffered.length - 1)
                const start = Math.max(end - 3, sourceBufferRef.current.buffered.start(0))
                if (start > sourceBufferRef.current.buffered.start(0)) {
                  sourceBufferRef.current.remove(0, start)
                }
              }
            }

            sourceBufferRef.current.addEventListener('updateend', onUpdateEnd)
            updateEndHandlerRef.current = onUpdateEnd
          }
        } catch (err) {
          const error = err as DOMException
          console.error('Ошибка создания SourceBuffer. Кодеки не поддерживаются?', error)
          setStatus(`Ошибка инициализации потока: ${error.message}`)
        }
        break

      case 'webrtc/candidate':
        // Сервер прислал ICE candidate — добавляем его в PeerConnection
        if (msg.value && pcRef.current) {
          pcRef.current
            .addIceCandidate(
              new RTCIceCandidate({
                candidate: msg.value,
                sdpMid: msg.sdpMid ?? '0',
                sdpMLineIndex: msg.sdpMLineIndex ?? 0,
              })
            )
            .catch(e => console.error('[WebRTC] Failed to add ICE candidate:', e))
        }
        break

      case 'webrtc/answer':
        // Сервер прислал SDP answer — устанавливаем его как remote description
        if (msg.value && pcRef.current) {
          pcRef.current
            .setRemoteDescription(new RTCSessionDescription({ type: 'answer', sdp: msg.value }))
            .catch(e => console.error('[WebRTC] Failed to set remote description:', e))
        }
        break

      default:
        break
    }
  }

  const handleBinaryData = (data: ArrayBuffer) => {
    if (!mediaSourceRef.current || !sourceBufferRef.current) return

    // Пытаемся добавить кадр напрямую. Если SourceBuffer занят — кадр теряется,
    // следующий keyframe восстановит картинку.
    try {
      sourceBufferRef.current.appendBuffer(data)
    } catch {
      // SourceBuffer занят — пропускаем кадр
    }
  }

  const setupMSE = () => {
    if (!window.MediaSource || !videoRef.current) return

    const mediaSource = new MediaSource()
    mediaSourceRef.current = mediaSource

    mediaSource.addEventListener('sourceopen', () => {
      if (!mountedRef.current) return
      console.debug('[MSE] MediaSource открыт. Запрашиваем кодеки у сервера.')
      send({ type: 'mse' })
    })

    videoRef.current.muted = true
    videoRef.current.src = URL.createObjectURL(mediaSource)
    safePlay()
  }

  const setupWebRTC = async (): Promise<boolean> => {
    const config: RTCConfiguration = {
      bundlePolicy: 'max-bundle',
      iceServers: [],
    }

    const pc = new RTCPeerConnection(config)
    pcRef.current = pc

    // Добавляем video transceiver, чтобы SDP offer содержал медиа-секции
    // direction: 'recvonly' — мы только принимаем видео (аудио не нужно)
    pc.addTransceiver('video', { direction: 'recvonly' })

    pc.onicecandidate = (ev: RTCPeerConnectionIceEvent) => {
      if (!mountedRef.current) return
      if (ev.candidate) {
        send({ type: 'webrtc/candidate', value: ev.candidate.candidate })
      }
    }

    pc.onicecandidateerror = (ev: Event) => {
      if (!mountedRef.current) return
      const iceEv = ev as RTCPeerConnectionIceErrorEvent
      // Ошибка отдельного ICE кандидата (например, STUN timeout) — не фатальна.
      // Браузер перебирает множество кандидатов, часть может не сработать.
      // Фатальные ошибки обрабатываются в oniceconnectionstatechange (state='failed').
      console.warn(`[WebRTC] ICE candidate error (code: ${iceEv.errorCode}): ${iceEv.errorText}`, {
        url: iceEv.url,
      })
    }

    pc.oniceconnectionstatechange = () => {
      if (!mountedRef.current) return
      const state = pc.iceConnectionState
      console.debug(`[WebRTC] ICE connection state: ${state}`)

      switch (state) {
        case 'failed':
          console.error('[WebRTC] ICE connection failed. Попытка ICE restart...')
          setStatus('ICE connection failed')
          setMode('ERROR')
          restartIce(pc)
          break
        case 'disconnected':
          console.warn('[WebRTC] ICE connection disconnected')
          setStatus('ICE connection lost')
          break
        case 'connected':
        case 'completed':
          setStatus('')
          // Используем modeRef, чтобы избежать stale closure
          if (modeRef.current === 'ERROR') {
            setMode('RTC')
          }
          break
        case 'closed':
          console.log('[WebRTC] ICE connection closed')
          break
        default:
          break
      }
    }

    pc.onconnectionstatechange = () => {
      if (!mountedRef.current) return
      const state = pc.connectionState
      console.debug(`[WebRTC] Connection state: ${state}`)

      switch (state) {
        case 'failed':
          console.error('[WebRTC] Peer connection failed')
          setStatus('Connection failed')
          setMode('ERROR')
          break
        case 'disconnected':
          console.warn('[WebRTC] Peer connection disconnected')
          setStatus('Connection lost')
          break
        case 'connected':
          console.log('[WebRTC] Peer connection established')
          setStatus('')
          setMode('RTC')
          break
        default:
          break
      }
    }

    pc.ontrack = (event: RTCTrackEvent) => {
      if (!mountedRef.current) return
      if (videoRef.current && event.streams && event.streams[0]) {
        console.debug('[VideoPlayer] WebRTC track received, switching from MSE to WebRTC')

        // Останавливаем MSE на сервере (go2rtc v1.9.14 поддерживает)
        send({ type: 'mse', value: '' })

        // Сбрасываем playPromise, чтобы ошибка от предыдущего play() (MSE) была проигнорирована
        playPromiseRef.current = null

        // Очищаем MSE перед переключением на WebRTC,
        // чтобы не было конфликта между video.src (MSE) и video.srcObject (WebRTC)
        resetMSE()
        videoRef.current.src = ''
        videoRef.current.srcObject = null
        videoRef.current.srcObject = event.streams[0]
        videoRef.current.muted = true
        safePlay()
        setMode('RTC')
      }
    }

    try {
      await pc.setLocalDescription(await pc.createOffer())
      send({ type: 'webrtc/offer', value: pc.localDescription?.sdp })
      return true
    } catch (e) {
      console.error('[WebRTC] Ошибка создания offer:', e)
      setStatus('Failed to create WebRTC offer')
      setMode('ERROR')
      return false
    }
  }

  /**
   * ICE restart: создаёт новый offer с флагом iceRestart и отправляет его на сервер.
   * Если restart не удался — переподключаемся через WebSocket.
   */
  const restartIce = async (pc: RTCPeerConnection) => {
    if (!mountedRef.current) return

    try {
      console.log('[WebRTC] ICE restart...')
      const offer = await pc.createOffer({ iceRestart: true })
      await pc.setLocalDescription(offer)
      send({ type: 'webrtc/offer', value: pc.localDescription?.sdp })
      setStatus('ICE restarting...')
    } catch (e) {
      console.error('[WebRTC] ICE restart failed:', e)
      pc.close()
      pcRef.current = null
      scheduleReconnect()
    }
  }

  return (
    <>
      <div className={styles.statusOverlay}>
        <span>{mode}</span>
        {status && <span className={styles.statusText}>({status})</span>}
      </div>

      <video ref={videoRef} controls playsInline autoPlay muted className={styles.video} />
    </>
  )
}

export default VideoStreamPlayer
